import React, { useRef } from 'react';
import ScoreBar from './ScoreBar';
import GroupDropdown from './GroupDropdown';
import { IconChevronDown } from '@tabler/icons-react';
import { formatDistanceToNow } from 'date-fns';
import { getCreatorDisplayName, getCreatorInitials } from '../../../../../utils/creatorHelpers';

const getStatusBadge = (status) => {
 switch (status) {
 case 'Approved':
 return { label: 'Approved', className: 'bg-accent-muted text-accent' };
 case 'High-Priority':
 case 'High Priority':
 case '(Persona) High Priority':
 return { label: 'High Pri.', className: 'bg-accent-muted text-accent' };
 case 'Pre-Approved':
 return { label: 'Pre-Appr.', className: 'bg-accent-muted text-accent' };
 case 'Strong Enough':
 return { label: 'Strong', className: 'bg-accent-muted text-accent' };
 case 'Strong Enough If We Need a Lot':
 return { label: 'Strong+', className: 'bg-accent-muted text-accent' };
 case 'Not Strong Enough':
 return { label: 'Weak', className: 'bg-warning-muted text-warning' };
 case 'Last-Resort':
 return { label: 'Last Res.', className: 'bg-warning-muted text-warning' };
 case 'Low Priority':
 case 'Persona Low Priority':
 return { label: 'Low Pri.', className: 'bg-warning-muted text-warning' };
 case 'BrikctoRecruit':
 return { label: 'Recruit', className: 'bg-accent-muted text-accent' };
 case 'In Reviewing':
 return { label: 'Reviewing', className: 'bg-warning-muted text-warning' };
 case 'NeedsToSet':
 case 'Not Set':
 default:
 return { label: 'Not Set', className: 'bg-surface-3 text-text-secondary' };
 }
};

const CreatorRow = ({ creator, isSelected, bulkCount = 0, onSelect, onGroupClick, groups, showDropdown, onGroupAssign, onUnassignCreator, onCreateNewForCreator, onCloseDropdown, creators }) => {
 const buttonRef = useRef(null);

 const cId = creator.creatorId || creator._id;
 const creatorGroupIds = creators
 .filter(c => (c.creatorId || c._id) === cId && c.groupId)
 .map(c => c.groupId);
 const groupCount = creatorGroupIds.length;

 // Build display label
 let groupLabel;
 if (groupCount === 0) {
 groupLabel = 'Not assigned';
 } else if (groupCount === 1) {
 const g = groups.find(g => g._id === creatorGroupIds[0]);
 groupLabel = g?.name || '1 group';
 } else {
 groupLabel = `${groupCount} groups`;
 }

 return (
 <tr className={`hover:bg-glass-bg-hover ${isSelected ? 'bg-accent-muted' : ''}`}>
 <td className="pl-3 pr-1 py-3">
 <input
 type="checkbox"
 checked={isSelected}
 onChange={(e) => onSelect(cId, e.target.checked)}
 className=" border-glass-border cursor-pointer text-accent focus:ring-accent"
 />
 </td>

 <td className="px-3 py-3">
 <div className="flex items-center min-w-0">
 {creator.profileImage ? (
 <img src={creator.profileImage} alt="" className="w-7 h-7 mr-2 object-cover flex-shrink-0" />
 ) : (
 <div className="w-7 h-7 bg-accent-muted flex items-center justify-center text-xs font-medium text-accent mr-2 flex-shrink-0">
 {getCreatorInitials(creator)}
 </div>
 )}
 <div className="min-w-0">
 <div className="text-sm font-medium text-text-primary truncate">{getCreatorDisplayName(creator)}</div>
 {creator.name && <div className="text-xs text-text-secondary truncate">{creator.name}</div>}
 </div>
 </div>
 </td>

 <td className="px-3 py-3 whitespace-nowrap">
 <ScoreBar score={creator.advancedMetrics?.score || 0} />
 </td>

 <td className="px-3 py-3 whitespace-nowrap">
 {(() => {
 const badge = getStatusBadge(creator.status);
 return (
 <span className={`inline-flex items-center px-1.5 py-0.5 text-xs font-medium ${badge.className}`} title={creator.status}>
 {badge.label}
 </span>
 );
 })()}
 </td>

 <td className="px-3 py-3 whitespace-nowrap text-sm text-text-secondary">
 {(creator.advancedMetrics?.smartFollowersCount || 0).toLocaleString()}
 </td>

 <td className="px-3 py-3 whitespace-nowrap text-sm text-text-secondary">
 {(creator.advancedMetrics?.impressionReach || 0).toLocaleString()}
 </td>

 <td className="px-3 py-3 whitespace-nowrap text-sm text-text-secondary">
 {creator.lastPaymentDate
 ? formatDistanceToNow(new Date(creator.lastPaymentDate), { addSuffix: true })
 : 'Never'}
 </td>

 <td className="px-3 py-3 whitespace-nowrap">
 <button
 ref={buttonRef}
 onClick={() => onGroupClick(cId)}
 className={`inline-flex items-center justify-between w-28 px-2 py-1.5 text-xs border ${
 bulkCount > 0
 ? 'border-warning bg-warning-muted text-warning'
 : groupCount > 0
 ? 'border-accent/30 bg-accent-muted text-accent'
 : 'border-glass-border bg-surface-2 text-text-secondary hover:bg-glass-bg-hover'
 }`}
 >
 <span className="truncate">{bulkCount > 0 ? `Assign ${bulkCount}` : groupLabel}</span>
 <IconChevronDown size={14} className="flex-shrink-0 ml-1 opacity-50" />
 </button>
 {showDropdown && (
 <GroupDropdown
 groups={groups}
 creators={creators}
 creatorGroupIds={creatorGroupIds}
 onSelect={(groupId) => onGroupAssign(cId, groupId)}
 onUnassign={(groupId) => onUnassignCreator(cId, groupId)}
 onCreateNew={() => onCreateNewForCreator(cId)}
 onClose={onCloseDropdown}
 anchorRef={buttonRef}
 />
 )}
 </td>
 </tr>
 );
};

export default CreatorRow;
